import { useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { logActivity } from "@/lib/activity-log";
import type { DeviceHashes } from "@/hooks/useDeviceHashes";

export interface Reminder {
  id: string;
  child_id: string;
  family_id: string;
  title: string;
  remind_date: string | null;
  remind_at: string | null;
  repeat_rule: string | null;
  dismissed_at: string | null;
  import_batch_id: string | null;
  original_text: string | null;
  category: string | null;
  created_by: string | null;
  created_at: string;
  seen_by_child_at: string | null;
  confirmed_at: string | null;
  confirmed_by: string | null;
}

export interface ReminderOccurrence {
  id: string;
  reminder_id: string;
  occurrence_date: string;
  seen_by_child_at: string | null;
  confirmed_at: string | null;
  confirmed_by: string | null;
}

/** Parse repeat_rule JSON and check if it matches a given ISO day (1=Mon..7=Sun) */
export function repeatRuleMatchesDay(repeatRule: string | null, isoDay: number): boolean {
  if (!repeatRule) return false;
  try {
    const rule = JSON.parse(repeatRule);
    if (rule.type === "daily") return true;
    if (rule.type === "weekly" && Array.isArray(rule.days)) {
      return rule.days.includes(isoDay);
    }
    return false;
  } catch {
    return false;
  }
}

/** Get the scheduled time for a repeating reminder on a given ISO day */
export function getRepeatTimeForDay(repeatRule: string | null, isoDay: number): string | null {
  if (!repeatRule) return null;
  try {
    const rule = JSON.parse(repeatRule);
    if (rule.time) return rule.time;
    if (rule.times && rule.times[String(isoDay)]) return rule.times[String(isoDay)];
    return null;
  } catch {
    return null;
  }
}

/** Get grace minutes from repeat rule (default 30) */
export function getRepeatGraceMinutes(repeatRule: string | null): number {
  if (!repeatRule) return 30;
  try {
    const rule = JSON.parse(repeatRule);
    return rule.graceMinutes ?? 30;
  } catch {
    return 30;
  }
}

export function useReminders(childId: string | undefined, deviceHashes?: DeviceHashes | null) {
  const queryClient = useQueryClient();
  const isDevice = !!deviceHashes;

  const { data: items = [], isLoading } = useQuery({
    queryKey: ["reminders", childId, isDevice ? "device" : "parent"],
    queryFn: async () => {
      if (!childId) return [];
      if (isDevice) {
        const { data, error } = await supabase.rpc("get_reminders_for_device", {
          _device_id_hash: deviceHashes.deviceIdHash,
          _session_token_hash: deviceHashes.sessionTokenHash,
        });
        if (error) throw error;
        return (data ?? []) as unknown as Reminder[];
      }
      const { data, error } = await supabase
        .from("reminders")
        .select("*")
        .eq("child_id", childId)
        .is("dismissed_at", null)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as unknown as Reminder[];
    },
    enabled: !!childId && (!isDevice || !!deviceHashes),
    refetchInterval: isDevice ? 5000 : undefined,
  });

  const { data: archivedItems = [], isLoading: isLoadingArchived } = useQuery({
    queryKey: ["reminders-archived", childId],
    queryFn: async () => {
      if (!childId) return [];
      const { data, error } = await supabase
        .from("reminders")
        .select("*")
        .eq("child_id", childId)
        .not("dismissed_at", "is", null)
        .order("dismissed_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as unknown as Reminder[];
    },
    enabled: !!childId && !isDevice, // archived not needed for child device
  });

  // Realtime
  useEffect(() => {
    if (!childId || isDevice) return; // device uses polling
    const channel = supabase
      .channel(`reminders-${childId}`)
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "reminders", filter: `child_id=eq.${childId}` },
        () => queryClient.invalidateQueries({ queryKey: ["reminders", childId] })
      )
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [childId, queryClient, isDevice]);

  const dismissReminder = useMutation({
    mutationFn: async (reminderId: string) => {
      const { error } = await supabase
        .from("reminders")
        .update({ dismissed_at: new Date().toISOString() })
        .eq("id", reminderId);
      if (error) throw error;
      const r = items.find((i) => i.id === reminderId) ?? archivedItems.find((i) => i.id === reminderId);
      if (r) {
        logActivity({ familyId: r.family_id, childId: r.child_id, action: "archived", entityType: "reminder", entityId: reminderId });
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["reminders", childId] });
      queryClient.invalidateQueries({ queryKey: ["reminders-archived", childId] });
    },
    onError: () => {
      toast.error("Kunne ikke avvise påminnelse");
    },
  });

  const deleteReminder = useMutation({
    mutationFn: async (reminderId: string) => {
      const r = items.find((i) => i.id === reminderId) ?? archivedItems.find((i) => i.id === reminderId);
      const { error } = await supabase
        .from("reminders")
        .delete()
        .eq("id", reminderId);
      if (error) throw error;
      if (r) {
        logActivity({ familyId: r.family_id, childId: r.child_id, action: "deleted", entityType: "reminder", entityId: reminderId, changes: { title: { old: r.title, new: null } } });
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["reminders", childId] });
      queryClient.invalidateQueries({ queryKey: ["reminders-archived", childId] });
    },
    onError: () => {
      toast.error("Kunne ikke slette påminnelse");
    },
  });

  const markSeen = useMutation({
    mutationFn: async (reminderId: string) => {
      const { error } = await supabase
        .from("reminders")
        .update({ seen_by_child_at: new Date().toISOString() })
        .eq("id", reminderId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["reminders", childId] });
    },
    onError: () => {
      toast.error("Kunne ikke markere som sett");
    },
  });

  const restoreReminder = useMutation({
    mutationFn: async (reminderId: string) => {
      const r = archivedItems.find((i) => i.id === reminderId);
      const { error } = await supabase
        .from("reminders")
        .update({ dismissed_at: null })
        .eq("id", reminderId);
      if (error) throw error;
      if (r) {
        logActivity({ familyId: r.family_id, childId: r.child_id, action: "restored", entityType: "reminder", entityId: reminderId });
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["reminders", childId] });
      queryClient.invalidateQueries({ queryKey: ["reminders-archived", childId] });
      toast.success("Påminnelse gjenopprettet");
    },
    onError: () => {
      toast.error("Kunne ikke gjenopprette påminnelse");
    },
  });

  return { items, archivedItems, isLoading, isLoadingArchived, dismissReminder, deleteReminder, markSeen, restoreReminder };
}

/**
 * Hook to load and manage per-day occurrences for repeating reminders.
 * Returns occurrence map and mutations to mark seen/confirmed.
 */
export function useReminderOccurrences(childId: string | undefined, date: string | undefined, deviceHashes?: DeviceHashes | null) {
  const queryClient = useQueryClient();
  const isDevice = !!deviceHashes;

  // Fetch all occurrences for reminders belonging to this child on this date
  const { data: occurrences = [] } = useQuery({
    queryKey: ["reminder-occurrences", childId, date, isDevice ? "device" : "parent"],
    queryFn: async () => {
      if (!childId || !date) return [];
      if (isDevice) {
        const { data, error } = await supabase.rpc("get_reminder_occurrences_for_device", {
          _device_id_hash: deviceHashes.deviceIdHash,
          _session_token_hash: deviceHashes.sessionTokenHash,
          _date: date,
        });
        if (error) throw error;
        return (data ?? []).map((d: any) => ({
          id: d.id,
          reminder_id: d.reminder_id,
          occurrence_date: d.occurrence_date,
          seen_by_child_at: d.seen_by_child_at,
          confirmed_at: d.confirmed_at,
          confirmed_by: d.confirmed_by,
        })) as ReminderOccurrence[];
      }
      const { data, error } = await supabase
        .from("reminder_occurrences")
        .select("*, reminders!inner(child_id)")
        .eq("reminders.child_id", childId)
        .eq("occurrence_date", date);
      if (error) throw error;
      return (data ?? []).map((d: any) => ({
        id: d.id,
        reminder_id: d.reminder_id,
        occurrence_date: d.occurrence_date,
        seen_by_child_at: d.seen_by_child_at,
        confirmed_at: d.confirmed_at,
        confirmed_by: d.confirmed_by,
      })) as ReminderOccurrence[];
    },
    enabled: !!childId && !!date && (!isDevice || !!deviceHashes),
    refetchInterval: isDevice ? 5000 : undefined,
  });

  // Build lookup: reminder_id -> occurrence
  const occurrenceMap = new Map<string, ReminderOccurrence>();
  for (const occ of occurrences) {
    occurrenceMap.set(occ.reminder_id, occ);
  }

  // Realtime for occurrences (parent only)
  useEffect(() => {
    if (!childId || !date || isDevice) return;
    const channel = supabase
      .channel(`reminder-occ-${childId}-${date}`)
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "reminder_occurrences" },
        () => queryClient.invalidateQueries({ queryKey: ["reminder-occurrences", childId, date] })
      )
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [childId, date, queryClient, isDevice]);

  const markOccurrenceSeen = useMutation({
    mutationFn: async ({ reminderId, occurrenceDate }: { reminderId: string; occurrenceDate: string }) => {
      if (isDevice) {
        const { error } = await supabase.rpc("mark_reminder_seen_for_device", {
          _device_id_hash: deviceHashes!.deviceIdHash,
          _session_token_hash: deviceHashes!.sessionTokenHash,
          _reminder_id: reminderId,
          _occurrence_date: occurrenceDate,
        });
        if (error) throw error;
        return;
      }
      const now = new Date().toISOString();
      const { error } = await supabase
        .from("reminder_occurrences")
        .upsert(
          { reminder_id: reminderId, occurrence_date: occurrenceDate, seen_by_child_at: now },
          { onConflict: "reminder_id,occurrence_date" }
        );
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["reminder-occurrences", childId, date] });
      queryClient.invalidateQueries({ queryKey: ["reminders", childId] });
    },
  });

  /** Mark reminder as confirmed (child clicked "Gjort!") */
  const confirmReminder = useMutation({
    mutationFn: async ({ reminderId, occurrenceDate }: { reminderId: string; occurrenceDate: string }) => {
      if (isDevice) {
        const { error } = await supabase.rpc("confirm_reminder_for_device", {
          _device_id_hash: deviceHashes!.deviceIdHash,
          _session_token_hash: deviceHashes!.sessionTokenHash,
          _reminder_id: reminderId,
          _occurrence_date: occurrenceDate,
        });
        if (error) throw error;
        return;
      }
      // Parent/auth path - find the reminder and update directly
      const now = new Date().toISOString();
      // For repeating reminders, update occurrence
      const { error } = await supabase
        .from("reminder_occurrences")
        .upsert(
          { reminder_id: reminderId, occurrence_date: occurrenceDate, confirmed_at: now, seen_by_child_at: now },
          { onConflict: "reminder_id,occurrence_date" }
        );
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["reminder-occurrences", childId, date] });
      queryClient.invalidateQueries({ queryKey: ["reminders", childId] });
    },
  });

  /** Device-only: mark non-repeating reminder as seen via RPC */
  const markSeenDevice = useMutation({
    mutationFn: async ({ reminderId, occurrenceDate }: { reminderId: string; occurrenceDate: string }) => {
      if (!isDevice) return;
      const { error } = await supabase.rpc("mark_reminder_seen_for_device", {
        _device_id_hash: deviceHashes!.deviceIdHash,
        _session_token_hash: deviceHashes!.sessionTokenHash,
        _reminder_id: reminderId,
        _occurrence_date: occurrenceDate,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["reminder-occurrences", childId, date] });
      queryClient.invalidateQueries({ queryKey: ["reminders", childId] });
    },
  });

  /** Device-only: confirm non-repeating reminder via RPC */
  const confirmReminderDevice = useMutation({
    mutationFn: async ({ reminderId, occurrenceDate }: { reminderId: string; occurrenceDate: string }) => {
      if (!isDevice) return;
      const { error } = await supabase.rpc("confirm_reminder_for_device", {
        _device_id_hash: deviceHashes!.deviceIdHash,
        _session_token_hash: deviceHashes!.sessionTokenHash,
        _reminder_id: reminderId,
        _occurrence_date: occurrenceDate,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["reminder-occurrences", childId, date] });
      queryClient.invalidateQueries({ queryKey: ["reminders", childId] });
    },
  });

  return { occurrenceMap, markOccurrenceSeen, confirmReminder, markSeenDevice, confirmReminderDevice };
}
